import React, { useMemo, useState } from "react";
import { Avatar, Badge, Button, EmployeeAvatar, Icon, Input } from "../../../design-system";

/**
 * Selector multi-empleado con chips para los seleccionados y lista
 * filtrable para añadir. Mantiene la misma estética que EmployeePicker
 * (avatar + nombre + cargo) — diferencia: aquí `value` es lista de IDs
 * y `onChange` recibe la lista actualizada.
 *
 * UX:
 *  - Click sobre un empleado → toggle (añade si falta, quita si ya está).
 *  - Chip "X" en cada seleccionado → quita uno.
 *  - Botón "Seleccionar todos" / "Limpiar" sobre la lista filtrada.
 *
 * @param {{
 *  employees: Array<{ id: string, first_name?: string, last_name?: string, position?: string, email?: string, avatar_path?: string|null }>,
 *  value: string[],
 *  onChange: (ids: string[]) => void,
 *  placeholder?: string,
 *  disabled?: boolean,
 * }} props
 */
export function EmployeeMultiPicker({
  employees = [],
  value = [],
  onChange,
  placeholder = "Busca por nombre, cargo o email…",
  disabled = false,
}) {
  const [q, setQ] = useState("");
  const selectedSet = useMemo(() => new Set(value || []), [value]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((e) => {
      const hay = [`${e.first_name || ""} ${e.last_name || ""}`, e.position, e.email]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(term);
    });
  }, [employees, q]);

  const selectedItems = useMemo(
    () => (value || []).map((id) => employees.find((e) => e.id === id)).filter(Boolean),
    [value, employees],
  );

  const toggle = (id) => {
    if (disabled) return;
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange?.(Array.from(next));
  };

  const selectAllFiltered = () => {
    if (disabled) return;
    const next = new Set(selectedSet);
    for (const e of filtered) next.add(e.id);
    onChange?.(Array.from(next));
  };

  const clear = () => onChange?.([]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        border: "1px solid var(--kr-border)",
        borderRadius: 8,
        padding: 10,
        background: "#fff",
      }}
    >
      {selectedItems.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {selectedItems.map((emp) => (
            <Chip key={emp.id} emp={emp} onRemove={() => toggle(emp.id)} disabled={disabled} />
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <div style={{ flex: 1 }}>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            icon="Search"
            disabled={disabled}
          />
        </div>
        <Badge tone={selectedItems.length > 0 ? "info" : "neutral"}>
          {selectedItems.length} seleccionados
        </Badge>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={selectAllFiltered}
          disabled={disabled || filtered.length === 0}
        >
          Seleccionar {q.trim() ? "filtrados" : "todos"} ({filtered.length})
        </Button>
        {selectedItems.length > 0 && (
          <Button variant="ghost" size="sm" type="button" onClick={clear} disabled={disabled}>
            Limpiar
          </Button>
        )}
      </div>

      <div
        style={{
          maxHeight: 260,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {filtered.length === 0 ? (
          <div
            style={{
              padding: "16px 8px",
              fontSize: 12,
              color: "var(--kr-fg-muted)",
              textAlign: "center",
            }}
          >
            <Icon name="UserX" size={16} style={{ display: "block", margin: "0 auto 6px", opacity: 0.6 }} />
            Sin coincidencias
          </div>
        ) : (
          filtered.map((emp) => {
            const isSelected = selectedSet.has(emp.id);
            return (
              <button
                key={emp.id}
                type="button"
                onClick={() => toggle(emp.id)}
                disabled={disabled}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 10px",
                  background: isSelected ? "var(--kr-bg-subtle)" : "transparent",
                  border: "none",
                  borderRadius: 6,
                  cursor: disabled ? "default" : "pointer",
                  textAlign: "left",
                  width: "100%",
                }}
                onMouseEnter={(e) => {
                  if (!disabled && !isSelected) e.currentTarget.style.background = "var(--kr-bg-subtle)";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = "transparent";
                }}
              >
                <EmployeeAvatar
                  employeeId={emp.id}
                  hasAvatar={!!emp.avatar_path}
                  name={fullName(emp) || "?"}
                  size={32}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: "var(--kr-fg-strong)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {fullName(emp) || "(sin nombre)"}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--kr-fg-muted)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {emp.position || emp.email || "—"}
                  </div>
                </div>
                {isSelected && (
                  <Icon name="Check" size={16} style={{ color: "var(--kr-success-600)" }} />
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function Chip({ emp, onRemove, disabled }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 4px 4px 6px",
        background: "var(--kr-bg-subtle)",
        border: "1px solid var(--kr-border)",
        borderRadius: 999,
        fontSize: 12,
      }}
    >
      <EmployeeAvatar employeeId={emp.id} hasAvatar={!!emp.avatar_path} name={fullName(emp) || "?"} size={20} />
      <span style={{ color: "var(--kr-fg-strong)", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {fullName(emp) || "(sin nombre)"}
      </span>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Quitar ${fullName(emp)}`}
        style={{
          width: 18,
          height: 18,
          borderRadius: 999,
          border: "none",
          background: "transparent",
          cursor: disabled ? "default" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--kr-fg-muted)",
        }}
      >
        <Icon name="X" size={12} />
      </button>
    </div>
  );
}

function fullName(e) {
  return `${e.first_name || ""} ${e.last_name || ""}`.trim();
}

export default EmployeeMultiPicker;
